import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import CircularLoader from '../../common/CircularLoader';
import Banner from '../../common/Banner';
import { BaseRepository } from '../../../repositories/base_repository/base_repository';
import { toast } from 'react-toastify';

const InvoiceUploader = ({ onUpload, statementRef }) => {
  const user = useSelector((state) => state.user.user);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [fileName, setFileName] = useState('');

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];

    setError('');
    setFileName('');

    if (!file) return;

    if (file.type !== 'application/pdf') {
      setError('Please upload a PDF file only');
      return;
    }

    // Check file size (4MB = 4,194,304 bytes)
    if (file.size > 4194304) {
      setError('File size must be less than 4MB');
      return;
    }

    if (!user) {
      setError('Please login and try again');
      return;
    }

    setIsLoading(true);
    setFileName(file.name);

    try {
      await BaseRepository.sendInvoice(file, user, statementRef);
      toast.success('Invoice has successfully sent');
      onUpload();
    } catch (err) {
      setError('Failed to upload file. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const handleDrop = (event) => {
    event.preventDefault();
    const file = event.dataTransfer.files?.[0];
    if (file) {
      // Trigger file upload handling
      const input = document.getElementById('dropzone-file');
      const dataTransfer = new DataTransfer();
      dataTransfer.items.add(file);
      input.files = dataTransfer.files;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  };

  return (
    <div className="w-full">
      {error && (
        <div className="pb-2">
          <Banner type={'warning'}>{error}</Banner>
        </div>
      )}

      <div className="flex items-center justify-center w-full">
        <label
          htmlFor="dropzone-file"
          className="flex flex-col items-center justify-center w-full min-h-36 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 relative"
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          <div className="flex flex-col items-center justify-center pt-5 pb-6">
            {isLoading && <CircularLoader />}

            <h4 className="text-gray-500 font-bold mb-2">
              {fileName ? `Uploaded: ${fileName}` : 'Please upload your invoice'}
            </h4>
            {!isLoading && (
              <>
                <p className="mb-2 text-sm text-gray-500 font-medium">
                  Click to choose PDF (max 4mb)
                </p>
              </>
            )}
          </div>
          <input
            id="dropzone-file"
            type="file"
            className="hidden"
            accept=".pdf"
            onChange={handleFileUpload}
            disabled={isLoading}
          />
        </label>
      </div>
    </div>
  );
};
InvoiceUploader.propTypes = {
  onUpload: PropTypes.func.isRequired,
  statementRef: PropTypes.string.isRequired,
};

export default InvoiceUploader;
